import { useEffect, useState, useMemo } from "react";
import {
  Calendar,
  MapPin,
  Clock,
  Tag,
  Search,
  Loader2,
  Info,
} from "lucide-react";
import { eventsAPI, type JBFEvent } from "../../services/api";
import eventsHeroBg from "../../assets/eventBG.jpg"; // Reusing an existing asset for hero background
import { formatDate } from "@/projects/Jainboarding_Admin/lib/utils";
import { SitePagination } from "../ui/SitePagination";

const ITEMS_PER_PAGE = 6;

export default function EventsPage() {
  const [events, setEvents] = useState<JBFEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const categories = [
    "All",
    "Workshop",
    "Seminar",
    "Meeting",
    "Hostel",
    "Institution",
    "Boarding",
  ];

  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Dynamic fetch based on filters
  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = {};
        if (filter !== "All") {
          params.event_for = filter;
        }
        if (debouncedSearch) {
          params.search = debouncedSearch;
        }

        const data = await eventsAPI.getEventList(params);
        if (cancelled) {
          return;
        }
        setEvents(data);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch events:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [filter, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(events.length / ITEMS_PER_PAGE));

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return events.slice(start, start + ITEMS_PER_PAGE);
  }, [events, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, debouncedSearch]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(events.length / ITEMS_PER_PAGE));
    setCurrentPage(prev => (prev > tp ? tp : prev));
  }, [events.length]);

  return (
    <div className="min-h-screen bg-[#FFF9F0]">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={eventsHeroBg}
            alt="Events hero"
            className="h-[185%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="mb-4 text-center text-5xl font-bold lg:text-6xl">
              JBF Events
            </h1>
            <p className="text-xl leading-relaxed text-gray-100">
              Stay updated with our latest workshops, seminars, and community
              gatherings designed to foster growth, learning, and collaboration
              within the Jain hostel network.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <section className="sticky top-[8.9rem] z-20 border-b border-gray-100 bg-white/80 shadow-sm backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Search Bar */}
            <div className="relative w-full max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full rounded-2xl border border-gray-100 bg-gray-50/50 py-2 pl-11 pr-4 text-sm outline-none transition-all focus:border-[#7A1E1E]/30 focus:bg-white focus:ring-4 focus:ring-[#7A1E1E]/5"
              />
            </div>

            {/* Category Filters */}
            <div className="scrollbar-hide flex items-center gap-3 overflow-x-auto pb-2 md:pb-0">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`shrink-0 rounded-full border border-transparent px-5 py-1.5 text-sm transition-all ${filter === cat
                      ? "bg-[#7A1E1E] text-white shadow-md shadow-[#7A1E1E]/20"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="bg-gradient-to-b from-white to-[#FFF5E6] py-12 sm:py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
              <p className="font-medium text-gray-500">
                Fetching upcoming events...
              </p>
            </div>
          ) : events.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-gray-200">
              <Info className="h-12 w-12 text-gray-300" />
              <p className="text-xl font-medium text-gray-500">
                No events found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setFilter("All");
                  setSearchTerm("");
                }}
                className="font-semibold text-[#7A1E1E] hover:underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {paginatedEvents.map(event => (
                  <div
                    key={event.id}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg transition-all hover:-translate-y-1 hover:shadow-xl"
                  >
                    {/* Event Image */}
                    <div className="relative h-56 w-full shrink-0 overflow-hidden bg-gray-100">
                      {event.image ? (
                        <img
                          src={event.image}
                          alt={event.event_title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-gray-100">
                          <Tag className="h-12 w-12 text-gray-200" />
                        </div>
                      )}
                      <div className="absolute left-4 top-4">
                        <div className="flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-[#7A1E1E] shadow-sm ring-1 ring-[#7A1E1E]/10 backdrop-blur-sm">
                          <Tag className="h-3.5 w-3.5" />
                          {event.event_for}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="mb-3 line-clamp-2 min-h-[3.5rem] text-xl font-bold text-gray-900 transition-colors group-hover:text-[#7A1E1E]">
                        {event.event_title}
                      </h3>

                      <p className="mb-6 line-clamp-3 text-sm leading-relaxed text-gray-600">
                        {event.description}
                      </p>

                      <div className="mt-auto space-y-3 border-t border-gray-100 pt-6">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="shrink-0 rounded-lg bg-[#7A1E1E]/5 p-2">
                              <Calendar className="h-4 w-4 text-[#7A1E1E]" />
                            </div>
                            {event.start_date && event.end_date && (
                              <div className="flex flex-wrap items-baseline gap-x-2">
                                <span className="text-sm font-bold text-gray-800">
                                  {formatDate(
                                    event.start_date ?? event.date ?? "-"
                                  )}
                                </span>
                                <span className="text-[10px] font-black uppercase text-gray-400">
                                  To
                                </span>
                                <span className="text-sm font-bold text-gray-800">
                                  {formatDate(
                                    event.end_date ?? event.date ?? "-"
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-[11px] font-bold text-blue-600">
                            <Clock className="h-3 w-3" />
                            {event.number_of_days} Days
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="shrink-0 rounded-lg bg-amber-50 p-2">
                            <MapPin className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="space-y-0.5">
                            <span className="line-clamp-1 text-sm font-semibold text-gray-700">
                              {event.address}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <SitePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={events.length}
                  pageSize={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                  itemLabel="events"
                />
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
